/**
 * Keyboard → the game, for desktop play. Held keys drive movement each frame;
 * edge-triggered keys fire actions once. Nothing reaches the game while a text
 * field has focus, so the talk box still takes typing (Esc leaves it).
 */
const NAV = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space']);

function typing(): boolean {
  const a = document.activeElement;
  return a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement || (a instanceof HTMLElement && a.isContentEditable);
}

export class Keyboard {
  private down = new Set<string>();
  private pressed = new Set<string>();
  private released = new Set<string>();
  /** a movement key was pressed (the cue to enter walk mode) */
  onMove?: () => void;
  /** Enter with nothing focused: the player wants to type */
  onTalk?: () => void;

  constructor() {
    window.addEventListener('keydown', (e) => {
      if (typing()) {
        if (e.code === 'Escape') (document.activeElement as HTMLElement).blur();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (NAV.has(e.code)) e.preventDefault();
      if (e.repeat) return;
      this.down.add(e.code);
      this.pressed.add(e.code);
      if (e.code === 'Enter') this.onTalk?.();
      else if (NAV.has(e.code) && e.code !== 'Space') this.onMove?.();
    });
    window.addEventListener('keyup', (e) => {
      if (this.down.has(e.code)) this.released.add(e.code);
      this.down.delete(e.code);
    });
    window.addEventListener('blur', () => this.down.clear());
  }

  held(code: string): boolean {
    return this.down.has(code);
  }
  /** true once, on the frame the key went down */
  took(code: string): boolean {
    return this.pressed.delete(code);
  }
  /** true once, on the frame the key came up */
  let_go(code: string): boolean {
    return this.released.delete(code);
  }
  /** call at the end of each frame so edges last exactly one frame */
  endFrame(): void {
    this.pressed.clear();
    this.released.clear();
  }

  /** x: strafe right (+1) / left (−1) with A/D; y: forward (+1) / back (−1) with W/S or ↑/↓ */
  get axis(): { x: number; y: number } {
    const x = (this.held('KeyD') ? 1 : 0) - (this.held('KeyA') ? 1 : 0);
    const y = (this.held('KeyW') || this.held('ArrowUp') ? 1 : 0) - (this.held('KeyS') || this.held('ArrowDown') ? 1 : 0);
    return { x, y };
  }
  /** turn right (+1) / left (−1) with ← → (the mouse and a drag turn too) */
  get turn(): number {
    return (this.held('ArrowRight') ? 1 : 0) - (this.held('ArrowLeft') ? 1 : 0);
  }
  get run(): boolean {
    return this.held('ShiftLeft') || this.held('ShiftRight');
  }
}
